import React from "react";

export interface InputProps {
  label?: string;
  description?: string;
  error?: string;
  required?: boolean;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  showCounter?: boolean;
  maxLength?: number;
  disabled?: boolean;
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  id?: string;
  name?: string;
  type?: string;
  style?: React.CSSProperties;
}

type InputState = "default" | "filled" | "focused" | "error" | "disabled";

function getBoxStyles(state: InputState): React.CSSProperties {
  switch (state) {
    case "default":
      return { background: "var(--color-bg-accent)", border: "1px solid transparent" };
    case "filled":
      return { background: "var(--color-bg-primary)", border: "1px solid var(--color-border-primary)" };
    case "focused":
      return { background: "var(--color-bg-primary)", border: "1px solid var(--color-border-brand)" };
    case "error":
      return { background: "var(--color-bg-primary)", border: "1px solid var(--color-border-danger)" };
    case "disabled":
      return { background: "var(--color-bg-accent)", border: "1px solid transparent", opacity: 0.6 };
  }
}

const textStyle: React.CSSProperties = {
  fontFamily: "var(--font-family-base)",
  fontSize: "var(--font-size-label-s)",
  fontWeight: "var(--font-weight-medium)",
  lineHeight: "var(--line-height-label-s)",
};

const WarningIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 2L14.5 13.5H1.5L8 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M8 6.5V9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="8" cy="11.5" r="0.75" fill="currentColor" />
  </svg>
);

export function Input({
  label,
  description,
  error,
  required,
  leadingIcon,
  trailingIcon,
  showCounter = false,
  maxLength,
  disabled,
  placeholder,
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  id,
  name,
  type = "text",
  style,
}: InputProps) {
  const uidRef = React.useRef(`makula-input-${Math.random().toString(36).slice(2)}`);
  const inputId = id ?? uidRef.current;

  const [focused, setFocused] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState(defaultValue ?? "");

  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;

  const state: InputState = disabled
    ? "disabled"
    : error
    ? "error"
    : focused
    ? "focused"
    : currentValue
    ? "filled"
    : "default";

  const charCount = currentValue.length;
  const iconColor =
    state === "error" ? "var(--color-content-danger)" : "var(--color-content-secondary)";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-s)", width: "100%", ...style }}>
      {/* Placeholder color can't be set via inline styles — use a scoped style tag */}
      <style>{`#${CSS.escape(inputId)}::placeholder { color: var(--color-content-secondary, #748094); }`}</style>

      {label && (
        <label
          htmlFor={inputId}
          style={{
            ...textStyle,
            display: "flex",
            gap: "var(--spacing-2xs)",
            alignItems: "center",
            color: "var(--color-content-secondary)",
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? "not-allowed" : "default",
          }}
        >
          {label}
          {required && <span aria-hidden="true">*</span>}
        </label>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "var(--spacing-m)",
          borderRadius: "var(--radius-s)",
          gap: "var(--spacing-s)",
          boxSizing: "border-box",
          transition: "border-color 120ms ease, background-color 120ms ease",
          ...getBoxStyles(state),
        }}
      >
        {leadingIcon && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              flexShrink: 0,
              width: 16,
              height: 16,
              color: iconColor,
            }}
          >
            {leadingIcon}
          </span>
        )}

        <input
          id={inputId}
          name={name}
          type={type}
          disabled={disabled}
          placeholder={placeholder}
          value={isControlled ? value : internalValue}
          maxLength={maxLength}
          style={{
            ...textStyle,
            flex: 1,
            minWidth: 0,
            background: "transparent",
            border: "none",
            outline: "none",
            color: "var(--color-content-primary)",
            cursor: disabled ? "not-allowed" : "text",
          }}
          onChange={(e) => {
            if (!isControlled) setInternalValue(e.target.value);
            onChange?.(e);
          }}
          onFocus={(e) => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
        />

        {trailingIcon && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              flexShrink: 0,
              width: 16,
              height: 16,
              color: iconColor,
            }}
          >
            {trailingIcon}
          </span>
        )}
      </div>

      {/* Error row */}
      {state === "error" && error && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "var(--spacing-xs)",
            paddingInline: "var(--spacing-2xs)",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              flexShrink: 0,
              width: 16,
              height: 16,
              color: "var(--color-content-danger)",
            }}
          >
            <WarningIcon />
          </span>
          <span
            style={{
              ...textStyle,
              flex: 1,
              color: "var(--color-content-danger)",
            }}
          >
            {error}
          </span>
          {showCounter && maxLength !== undefined && (
            <span
              style={{
                ...textStyle,
                color: "var(--color-content-danger)",
                textAlign: "right",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {charCount}/{maxLength}
            </span>
          )}
        </div>
      )}

      {/* Description / counter row (non-error) */}
      {state !== "error" && (description || (showCounter && maxLength !== undefined)) && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "var(--spacing-s)",
          }}
        >
          {description && (
            <span
              style={{
                ...textStyle,
                flex: 1,
                color: "var(--color-content-secondary)",
              }}
            >
              {description}
            </span>
          )}
          {showCounter && maxLength !== undefined && (
            <span
              style={{
                ...textStyle,
                color: "var(--color-content-secondary)",
                textAlign: "right",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {charCount}/{maxLength}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
